import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx"
import { createContext, useContext } from 'react'

export class VirtualboardStore {
    elements = {};
    undo = []
    redo = []
    zoom = 1;
    pan = { x: 0, y: 0 };
    eventsStackTail = {}
    events = {}
    backgroundImage = null;
    imageSize = { width: 0, height: 0 };
    containerState = { hideTools: false, fullscreen: false, lockToTool: true };
    tool = "pen"
    dimentions = {
        canvas: { width: 0, height: 0 },
        container: { width: 0, height: 0 },
    }
    cursor = {
        page: { x: 0, y: 0 },
        canvas: { x: 0, y: 0 }
    }
    readOnlyBoardState = {}

    boardGlobalSettings = {
        color: { name: 'Red', r: 230, g: 25, b: 75, hex: '#e6194b' },
        lineWidthScale: 1,
        opacity: 1,
        lineDash: [],
        lineJoin: "round",
        fill: false,
        stroke: true,
        strokeType: "solid",
        fontSize: 16,
    }

    boardState = {
        currentSlide: 0,
        slides: [],
        currentDocument: 0,
        document: {},
        selected: new Set()
    }


    constructor () {

        makeObservable( this, {
            boardGlobalSettings: observable,
            backgroundImage: observable,
            containerState: observable,
            boardState: observable,
            dimentions: observable,
            imageSize: observable,
            elements: observable,
            events: observable,
            cursor: observable,
            tool: observable,
            zoom: observable,
            pan: observable,

            updateBoardGlobalSettings: action,
            setReadOnlyBoardData: action,
            setContainerState: action,
            resetObservables: action,
            removeSelected: action,
            updateElements: action,
            setBackground: action,
            setDimentions: action,
            setBoardState: action,
            loadEvents: action,
            addElement: action,
            setElements: action,
            setCursor: action,
            setZoom: action,
            setTool: action,
            addEvent: action,
            setPan: action,
            clear: action,

            getReadOnlyBoardCurrentSlideElements: computed,
            getReadOnlyBoardCurrentSlide: computed,
            getBoardGlobalSettings: computed,
            getCompleteCanvasState: computed,
            getContainerState: computed,
            getBoardState: computed,
            getDimentions: computed,
            getElements: computed,
            getCursor: computed,
            getTool: computed,
            getZoom: computed,
            getPan: computed,
        } )
    }


    // EVENT STACK, UNDO and REDO

    resetObservables () {
        runInAction( () => {
            this.boardState = {
                currentSlide: 0,
                slides: [],
                currentDocument: 0,
                document: {},
                selected: new Set()
            }
            this.boardGlobalSettings = {
                color: { name: 'Red', r: 230, g: 25, b: 75, hex: '#e6194b' },
                lineWidthScale: 1,
                opacity: 1,
                lineDash: [],
                lineJoin: "round",
                fill: false,
                stroke: true,
                strokeType: "solid",
                fontFamily: "Montserrat"
            }
            this.elements = {}
            this.events = []
            this.undo = []
            this.redo = []
            this.zoom = 1
            this.pan = { x: 0, y: 0 }
            this.eventsStackTail = {}
            this.events = {}
            this.backgroundImage = null
            this.imageSize = { width: 0, height: 0 }
            this.containerState = { hideTools: false, fullscreen: false, lockToTool: true }
            this.tool = "pen"
            this.dimentions = {
                canvas: { width: 0, height: 0 },
                container: { width: 0, height: 0 },
            }
            this.cursor = {
                page: { x: 0, y: 0 },
                canvas: { x: 0, y: 0 }
            }
        } )
    }

    constructElements ( list, lastIndex ) {
        const elementMap = new Map()

        for ( let i = 0; i <= lastIndex; i++ ) {
            const event = list[i]
            if ( !event ) continue

            switch ( event.type ) {
                case "elements-loaded":
                    event.elements?.forEach( el => elementMap.set( el.id, el ) )
                    break
                case "element-added":
                case "elements-moved":
                case "elements-updated":
                    // Handles both single element and array of elements
                    const items = Array.isArray( event.elements ) ? event.elements : [event.element]
                    items.forEach( el => {
                        if ( el && el.id ) elementMap.set( el.id, el )
                    } )
                    break
                case "elements-removed":
                    event.elements?.forEach( id => elementMap.delete( id ) )
                    break
                case "clear-canvas":
                    elementMap.clear()
                    break
            }
        }
        return Array.from( elementMap.values() )
    }

    loadEvents ( events ) {
        runInAction( () => {
            this.events = events
        } )
        for ( const key in events ) {
            if ( !Object.hasOwn( events, key ) ) continue
            const findIndex = this.boardState.document.slides?.findIndex( slide => slide.id === key )
            if ( findIndex >= 0 )
                this.elements[key] = this.constructElements( this.events[this.boardState.document.slides[findIndex].id], this.events[this.boardState.document.slides[findIndex].id].length - 1 )
        }
    }

    addEvent ( event ) {
        const { type } = event

        const slideId = this.boardState.document.slides[this.boardState.currentSlide].id
        let newEventAdded = true

        runInAction( () => {
            // Initialization
            if ( !this.events[slideId] ) this.events[slideId] = [{ type: "board-loaded" }]
            if ( !this.elements[slideId] ) this.elements[slideId] = []
            this.eventsStackTail[slideId] = this.eventsStackTail[slideId] ?? 0

            // 1. Handle History Truncation
            // If user performs a new action while in an "undone" state, remove redo history.
            if ( !["undo", "redo", "elements-loaded"].includes( type ) ) {
                if ( this.eventsStackTail[slideId] < this.events[slideId].length - 1 ) {
                    this.events[slideId] = this.events[slideId].slice( 0, this.eventsStackTail[slideId] + 1 )
                }
            }

            switch ( type ) {

                case "elements-loaded":
                    this.events[slideId].push( event )
                    this.eventsStackTail[slideId] = this.events[slideId].length - 1
                    this.elements[slideId] = this.constructElements( this.events[slideId], this.eventsStackTail[slideId] )
                    break

                case "clear-canvas":
                    this.events[slideId] = [{ type: "board-loaded" }]
                    this.elements[slideId] = []
                    this.eventsStackTail[slideId] = undefined
                    break

                case "element-added":

                    // if ( this.eventsStackTail[slideId] !== this.events[slideId].length - 1 ) {
                    //     for ( let i = this.events[slideId].length - 1; i > this.eventsStackTail[slideId]; i-- ) {
                    //         this.events[slideId].pop()
                    //     }
                    //     this.eventsStackTail[slideId] = this.events[slideId].length - 1
                    // }
                    this.elements[slideId].push( event.element )

                    this.events[slideId].push( event )
                    this.eventsStackTail[slideId] = this.events[slideId].length - 1
                    break

                case "elements-removed":
                    // if ( this.eventsStackTail[slideId] !== this.events[slideId].length - 1 ) {
                    //     for ( let i = this.events[slideId].length - 1; i > this.eventsStackTail[slideId]; i-- ) {
                    //         this.events[slideId].pop()
                    //     }
                    //     this.eventsStackTail[slideId] = this.events[slideId].length - 1
                    // }
                    event.elements.forEach( id => {
                        const idx = this.elements[slideId].findIndex( el => el.id === id )
                        if ( idx >= 0 ) this.elements[slideId].splice( idx, 1 )
                    } )
                    this.events[slideId].push( event )
                    this.eventsStackTail[slideId] = this.events[slideId].length - 1
                    break

                case "elements-moved":
                case "elements-updated":
                    event.elements.forEach( updatedEl => {
                        const idx = this.elements[slideId].findIndex( el => el.id === updatedEl.id )
                        if ( idx >= 0 ) {
                            this.elements[slideId][idx] = updatedEl
                        } else {
                            this.elements[slideId].push( updatedEl )
                        }
                    } )
                    this.events[slideId].push( event )
                    this.eventsStackTail[slideId] = this.events[slideId].length - 1
                    break

                case "undo":
                    // if ( this.eventsStackTail[slideId] > 0 && !this.events[slideId][this.eventsStackTail[slideId]].undoStop ) {
                    //     let newTail = this.eventsStackTail[slideId] - 1
                    //     for ( let index = this.eventsStackTail[slideId] - 1; index >= 0; index-- ) {
                    //         const prevEvent = this.events[slideId][index]
                    //         if ( !["undo", "redo"].includes( prevEvent.type ) ) {
                    //             newTail = index
                    //             console.log( "Fixed undo index", index )

                    //             break
                    //         }
                    //     }
                    //     console.log( toJS( newTail ), toJS( this.events[slideId] ) )

                    //     this.eventsStackTail[slideId] = newTail
                    //     this.boardState.selected = new Set()
                    //     const undoElements = this.constructElements( this.events[slideId], this.eventsStackTail[slideId] )
                    //     this.elements[slideId] = undoElements
                    //     break
                    // }
                    // newEventAdded = false
                    // break
                    if ( this.eventsStackTail[slideId] > 0 ) {
                        this.eventsStackTail[slideId]--
                        this.elements[slideId] = this.constructElements( this.events[slideId], this.eventsStackTail[slideId] )
                        this.boardState.selected = new Set()
                    } else {
                        newEventAdded = false
                    }
                    break

                // case "redo":
                // if ( this.eventsStackTail[slideId] < this.events[slideId].length - 1 ) {
                //     this.eventsStackTail[slideId] += 1

                //     this.boardState.selected = new Set()
                //     const redoElements = this.constructElements( this.events[slideId], this.eventsStackTail[slideId] )
                //     this.elements[slideId] = redoElements
                // } else {
                //     newEventAdded = false
                //     this.eventsStackTail[slideId] = this.events[slideId].length - 1
                // }
                // break

                case "redo":
                    if ( this.eventsStackTail[slideId] < this.events[slideId].length - 1 ) {
                        this.eventsStackTail[slideId]++
                        this.elements[slideId] = this.constructElements( this.events[slideId], this.eventsStackTail[slideId] )
                        this.boardState.selected = new Set()
                    } else {
                        newEventAdded = false
                    }
                    break

                default:
                    break
            }

        } )

        return newEventAdded ? toJS( event ) : null
    }


    updateBoardGlobalSettings ( updates ) {
        runInAction( () => {
            for ( const key in updates ) {
                if ( !Object.hasOwn( updates, key ) ) continue
                const newValue = updates[key]
                this.boardGlobalSettings[key] = newValue
                if ( key === 'strokeType' ) {
                    if ( updates[key] === 'solid' )
                        this.boardGlobalSettings.lineDash = []
                    else if ( updates[key] === 'dotted' )
                        this.boardGlobalSettings.lineDash = [1, 3]
                    else if ( updates[key] === 'dashed' )
                        this.boardGlobalSettings.lineDash = [10, 5]
                }
            }
        } )
    }

    moveSlideBy ( offset ) {
        const newCurrentSlide = this.boardState.currentSlide + offset
        if ( newCurrentSlide >= 0 && newCurrentSlide < this.boardState.document.slides.length ) {
            runInAction( () => {
                this.boardState.currentSlide = newCurrentSlide
            } )
            return newCurrentSlide
        } else
            return null
    }

    setBoardState ( updates ) {
        console.log( "BOARD STATE UPDATED", updates )

        runInAction( () => {
            for ( const key in updates ) {
                if ( !Object.hasOwn( updates, key ) ) continue
                const newValue = updates[key]
                this.boardState[key] = newValue
            }
            console.log( "UDPATED BOARD: ", toJS( this.boardState ) )

        } )
    }

    setContainerState ( updates ) {
        runInAction( () => {
            for ( const key in updates ) {
                if ( !Object.hasOwn( updates, key ) ) continue
                const newValue = updates[key]
                this.containerState[key] = newValue
            }
        } )
    }

    setBackground ( image, width, height ) {
        this.backgroundImage = image
        this.imageSize = { width, height }
    }

    setDimentions ( dimention, element = "canvas" ) {
        runInAction( () => {
            this.dimentions[element] = dimention
        } )
    }

    setElements ( elements = {} ) {
        runInAction( () => {
            this.elements = elements
        } )

        if ( this.boardState.document && this.boardState.document.slides && this.boardState.currentSlide ) {
            const id = this.boardState.document.slides[this.boardState.currentSlide].id
            this.addEvent( { type: "elements-loaded", elements: [...( elements[id] || [] )], undoStop: true } )
        }
    }

    updateElements ( elementsIds = [], changes ) {
        let updated = []
        runInAction( () => {
            for ( const id of elementsIds ) {
                const pageId = this.boardState.document.slides[this.boardState.currentSlide].id
                const elementIndex = this.elements[pageId].findIndex( el => el.id === id )

                if ( elementIndex < 0 )
                    return
                const updatedEle = this.elements[pageId][elementIndex]

                if ( updated.type === "text" && changes.text && ( changes.text || "" ).trim() === "" ) {
                    this.elements[pageId].splice( elementIndex, 1 )
                    return
                }

                for ( const key in changes ) {
                    if ( !Object.hasOwn( changes, key ) ) continue
                    updatedEle[key] = changes[key]
                }
                updated.push( updatedEle )
            }

        } )
        return {
            type: "elements-updated",
            elements: updated,
            versionId: new Date().getTime(),
            timestamp: new Date()
        }
    }

    addElement ( element ) {
        runInAction( () => {
            const id = this.boardState.document.slides[this.boardState.currentSlide].id
            if ( !this.elements[id] )
                this.elements[id] = []
            this.elements[id].push( element )
        } )

        return {
            type: "element-added",
            element: element,
            versionId: new Date().getTime(),
            timestamp: new Date()
        }
    }

    removeSelected ( selected ) {
        const removed = []
        const selectedElements = selected || this.boardState.selected
        runInAction( () => {
            if ( selectedElements.size > 0 ) {
                const pageId = this.boardState.document.slides[this.boardState.currentSlide].id
                for ( const selectedEle of selectedElements ) {
                    const eleIndex = this.elements[pageId].findIndex( el => selectedEle === el.id )

                    if ( eleIndex >= 0 ) {
                        removed.push( ...this.elements[pageId].splice( eleIndex, 1 ) )
                    }
                    selectedElements.delete( selectedEle )
                }
            }
        } )
        return {
            type: "elements-removed",
            elements: removed,
            versionId: new Date().getTime(),
            timestamp: new Date()
        }
    }

    clear () {
        const id = this.boardState.document.slides[this.boardState.currentSlide].id
        this.elements[id] = []
    }

    setCursor ( pos ) {
        runInAction( () => {
            this.cursor = pos
        } )
    }

    setZoom ( zoom ) {
        this.zoom = zoom
    }

    setTool ( tool ) {
        this.tool = tool
    }

    setPan ( x, y ) {
        this.pan = { x, y }
    }

    // READ ONLY BOARD
    setReadOnlyBoardData ( data ) {
        this.readOnlyBoardState = data
    }

    get getBoardGlobalSettings () {
        return toJS( this.boardGlobalSettings )
    }

    get getCompleteCanvasState () {
        const pageId = this.boardState.document.slides[this.boardState.currentSlide].id
        return ( {
            elements: toJS( this.elements[pageId] ),
            pan: toJS( this.pan ),
            zoom: toJS( this.zoom ),
            boardState: toJS( this.boardState ),

        } )
    }

    get getContainerState () {
        return toJS( this.containerState )
    }

    get getBoardState () {
        return toJS( this.boardState )
    }

    get getDimentions () {
        return toJS( this.dimentions )
    }

    get getElements () {
        const id = this.boardState.document.slides?.[this.boardState.currentSlide].id
        return toJS( this.elements[id] ?? [] )
    }

    get getCursor () {
        return toJS( this.pos )
    }

    get getTool () {
        return toJS( this.tool )
    }
    get getZoom () {
        return toJS( this.zoom )
    }

    get getPan () {
        return toJS( this.pan )
    }

    // READ ONLY BOARD
    get getReadOnlyBoardCurrentSlide () {
        const currentSlide = this.readOnlyBoardState?.slides?.find( slide => slide.id === this.readOnlyBoardState?.currentSlideId )
        return toJS( currentSlide )
    }

    get getReadOnlyBoardCurrentSlideElements () {
        const id = this.readOnlyBoardState?.slides?.find( slide => slide.id === this.readOnlyBoardState?.currentSlideId )?.id
        return toJS( this.elements[id] ?? [] )
    }

}
const VitualboardStoreInstance = new VirtualboardStore()

export default VitualboardStoreInstance
const VirtualboardContext = createContext( null )

export const VirtualboardProvider = ( { children, store } ) => {
    return (
        <VirtualboardContext.Provider value={store}>
            {children}
        </VirtualboardContext.Provider>
    )
}

export const useVirtualboardStore = () => {
    const store = useContext( VirtualboardContext )

    if ( !store ) {
        throw new Error( "useVirtualboardStore must be used within a VirtualboardProvider" )
    }
    return store
}
